import type {
  PersonaOpportunityScriptOutputType,
  PersonaOpportunityVideoReference,
  YoutubeFormat,
} from "@nexus/contracts";

import { authFetch } from "@/lib/api-client";

/** Scene row as returned by `toPersonaScriptDto`. */
export interface PersonaScriptSceneDto {
  id: string;
  scriptId: string;
  sceneNumber: number;
  sceneName: string;
  seeing: string;
  hearing: string;
  props: string;
  product: string | null;
  version: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One opening hook; position 1 is the primary that Scene 1 shows. */
export interface PersonaScriptHookDto {
  hookKey: string;
  position: number;
  voiceover: string;
  onScreenText: string | null;
  line: string | null;
  strategyLabel: string | null;
}

export interface PersonaScriptCreatedByDto {
  displayName: string;
  email: string | null;
  photoUrl: string | null;
}

/** Script identity + latest scenes — matches BFF `toPersonaScriptDto`. */
export interface PersonaScriptDto {
  id: string;
  personaOpportunityId: string;
  slotIndex: number;
  parentScriptId: string | null;
  hookStrategy: string | null;
  remainingFreeSlotCount: number;
  status: string;
  generationError: string | null;
  scriptGuidelines: string | null;
  adDescription: string | null;
  background: string | null;
  concept: string | null;
  duration: string | null;
  hook: string | null;
  presenter: string | null;
  product: string | null;
  rationale: string | null;
  originalBriefUrl: string | null;
  customAssetUploadId: string | null;
  customAssetKind: "SCRIPT" | "BRIEF" | null;
  script: string | null;
  sequence: string | null;
  storyboardStatus: string;
  storyboardImageKey: string | null;
  storyboardImages: unknown;
  storyboardImagePrompt: string | null;
  storyboardBeats: unknown;
  storyboardError: string | null;
  storyboardCreatedAt: string | null;
  storyboardPanelStatus: string;
  storyboardPanels: Array<{
    panelIndex: number;
    sceneIndex?: number;
    label: string;
    caption: string;
    imageUrl: string | null;
  }>;
  storyboardPanelError: string | null;
  isFavorite: boolean;
  /** Null or absent is Meta. */
  youtubeFormat?: "in_stream_16_9" | "shorts_9_16" | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  scenes: PersonaScriptSceneDto[];
  /** Primary first. Optional only for fixtures built before hooks existed; the BFF always sends it. */
  hooks?: PersonaScriptHookDto[];
  hookCount?: number;
  /**
   * True while a non-cancelled order line holds the script — hooks can then only
   * be edited in place, not added, removed or reordered. Same rule the BFF enforces.
   */
  isHookSetLocked?: boolean;
  /** Highest scene version on the script. History is hidden when this is <= 1. */
  versionCount: number;
  /** Origin creator of version 1. Null when generated by the engine or unresolved. */
  createdBy?: PersonaScriptCreatedByDto | null;
}

/**
 * List all scripts for a persona opportunity set.
 * `GET /persona-opportunity-sets/:setId/scripts`
 */
export async function fetchPersonaOpportunitySetScripts(
  setId: string,
): Promise<PersonaScriptDto[]> {
  const { data } = await authFetch<PersonaScriptDto[]>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts`,
  );
  return data ?? [];
}

/**
 * List scripts for one persona opportunity on a set.
 * `GET /persona-opportunity-sets/:setId/opportunities/:opportunityId/scripts`
 */
export async function fetchPersonaOpportunityScripts(
  setId: string,
  opportunityId: string,
): Promise<PersonaScriptDto[]> {
  const { data } = await authFetch<PersonaScriptDto[]>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/opportunities/${encodeURIComponent(opportunityId)}/scripts`,
  );
  return data ?? [];
}

export type PersonaScriptOutputType = PersonaOpportunityScriptOutputType;
export type PersonaScriptVideoReference = PersonaOpportunityVideoReference;

/** Amber POST body for `…/scripts/generate`. */
export interface GeneratePersonaOpportunityScriptsRequest {
  tenantId: string;
  idempotencyKey: string;
  personaOpportunityIds: string[];
  scriptId?: string;
  scriptGuidelines?: string;
  outputType?: PersonaScriptOutputType;
  scriptsPerAngle?: number;
  videoReference?: PersonaScriptVideoReference;
  guidelinesUploadId?: string;
  youtubeFormat?: YoutubeFormat;
}

/**
 * Kick off persona script generation (Amber Path).
 * `POST /persona-opportunity-sets/:setId/scripts/generate`
 */
export async function generatePersonaOpportunityScripts(
  setId: string,
  body: GeneratePersonaOpportunityScriptsRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/generate`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  if (!correlationId) {
    throw new Error("Generate scripts response missing correlationId");
  }
  return { correlationId };
}

/**
 * Extract guidelines text for the generate-scripts modal textarea.
 * `POST /persona-opportunity-sets/:setId/guidelines/extract`
 */
export async function extractPersonaSetGuidelines(
  setId: string,
  file: File,
  tenantId: string,
): Promise<{ text: string; uploadId: string | null }> {
  const form = new FormData();
  form.append("tenantId", tenantId);
  form.append("file", file);
  const { data } = await authFetch<{ text: string; uploadId?: string }>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/guidelines/extract`,
    { method: "POST", body: form },
  );
  return { text: data?.text ?? "", uploadId: data?.uploadId ?? null };
}

/**
 * Extract paste-file text for Import. Does not persist an upload.
 * `POST /persona-opportunity-sets/:setId/scripts/import/extract`
 */
export async function extractPersonaScriptImport(
  setId: string,
  file: File,
  tenantId: string,
): Promise<{ text: string }> {
  const form = new FormData();
  form.append("tenantId", tenantId);
  form.append("file", file);
  const { data } = await authFetch<{ text: string }>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/import/extract`,
    { method: "POST", body: form },
  );
  return { text: data?.text ?? "" };
}

export interface LegacyOpportunitySetListItem {
  id: string;
  name: string;
  completedScriptsCount: number;
}

export interface LegacyOpportunityScriptListItem {
  id: string;
  hook: string | null;
  concept: string | null;
  script: string | null;
}

export interface LegacyOpportunityScriptsPage {
  items: LegacyOpportunityScriptListItem[];
  nextCursor: string | null;
  totalCount: number;
}

/**
 * Tenant opportunity sets for the Import From Opportunities picker.
 * `GET /persona-opportunity-sets/legacy-opportunity-sets`
 */
export async function fetchLegacyOpportunitySets(): Promise<{
  items: LegacyOpportunitySetListItem[];
}> {
  const { data } = await authFetch<{ items: LegacyOpportunitySetListItem[] }>(
    "/persona-opportunity-sets/legacy-opportunity-sets",
  );
  return { items: data?.items ?? [] };
}

/**
 * Paginated scripts in one tenant opportunity set.
 * `GET /persona-opportunity-sets/legacy-opportunity-sets/:legacySetId/scripts`
 */
export async function fetchLegacyOpportunitySetScripts(
  legacySetId: string,
  params: { cursor?: string; limit?: number; search?: string } = {},
): Promise<LegacyOpportunityScriptsPage> {
  const query = new URLSearchParams();
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit != null) query.set("limit", String(params.limit));
  const search = params.search?.trim();
  if (search) query.set("search", search);
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const { data } = await authFetch<LegacyOpportunityScriptsPage>(
    `/persona-opportunity-sets/legacy-opportunity-sets/${encodeURIComponent(legacySetId)}/scripts${suffix}`,
  );
  return {
    items: data?.items ?? [],
    nextCursor: data?.nextCursor ?? null,
    totalCount: data?.totalCount ?? 0,
  };
}

export interface ImportPersonaScriptsFromLegacyRequest {
  tenantId: string;
  legacySetId: string;
  scriptIds: string[];
}

/**
 * Sync copy of selected legacy scripts onto Your Scripts.
 * `POST /persona-opportunity-sets/:setId/scripts/import/legacy`
 */
export async function importPersonaScriptsFromLegacy(
  setId: string,
  body: ImportPersonaScriptsFromLegacyRequest,
): Promise<{ importedIds: string[] }> {
  const { data } = await authFetch<{ importedIds: string[] }>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/import/legacy`,
    {
      method: "POST",
      body: JSON.stringify({
        tenantId: body.tenantId,
        legacySetId: body.legacySetId,
        scriptIds: body.scriptIds,
        idempotencyKey: crypto.randomUUID(),
      }),
    },
  );
  return { importedIds: data?.importedIds ?? [] };
}

export interface ImportPersonaScriptFromTextRequest {
  tenantId: string;
  text: string;
  /** Blank lets the worker infer the title from the text. */
  title?: string;
}

/**
 * Amber Path: persist one imported script from confirmed textarea text.
 * `POST /persona-opportunity-sets/:setId/scripts/import`
 */
export async function importPersonaBriefFromText(
  setId: string,
  body: ImportPersonaScriptFromTextRequest,
): Promise<{ correlationId: string }> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/import-brief`,
    {
      method: "POST",
      body: JSON.stringify({
        tenantId: body.tenantId,
        setId,
        text: body.text,
        title: body.title,
        idempotencyKey,
      }),
    },
  );
  if (!correlationId) {
    throw new Error("Import brief response missing correlationId");
  }
  return { correlationId };
}

export async function importPersonaScriptFromText(
  setId: string,
  body: ImportPersonaScriptFromTextRequest,
): Promise<{ correlationId: string }> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/import`,
    {
      method: "POST",
      body: JSON.stringify({
        tenantId: body.tenantId,
        setId,
        text: body.text,
        title: body.title,
        idempotencyKey,
      }),
    },
  );
  if (!correlationId) {
    throw new Error("Import script response missing correlationId");
  }
  return { correlationId };
}

export interface SuggestPersonaHookVariationsRequest {
  tenantId: string;
  idempotencyKey: string;
}

/**
 * Amber Path: generate three ephemeral hook suggestions for a parent script.
 * `POST /persona-opportunity-sets/:setId/scripts/:scriptId/hooks/suggest`
 */
export async function suggestPersonaHookVariations(
  setId: string,
  scriptId: string,
  body: SuggestPersonaHookVariationsRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/hooks/suggest`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  if (!correlationId) {
    throw new Error("Suggest hook variations response missing correlationId");
  }
  return { correlationId };
}

export interface ConfirmPersonaHookVariationsRequest {
  tenantId: string;
  idempotencyKey: string;
  correlationId: string;
  parentScriptId: string;
  keptIndexes: number[];
}

/**
 * Persist kept hook variants as additional scripts.
 * `POST /persona-opportunity-sets/:setId/scripts/:scriptId/hooks/confirm`
 */
export async function confirmPersonaHookVariations(
  setId: string,
  scriptId: string,
  body: ConfirmPersonaHookVariationsRequest,
): Promise<PersonaScriptDto[]> {
  const { data } = await authFetch<PersonaScriptDto[]>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/hooks/confirm`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  return data ?? [];
}

export interface AiEditPersonaScriptsRequest {
  tenantId: string;
  setId: string;
  scriptIds: string[];
  instruction: string;
  cell?: { sceneNumber: number; field: "seeing" | "hearing" | "product" | "props" };
}

/**
 * Amber Path: rewrite one or more persona scripts with an instruction.
 * `POST /persona-opportunity-sets/:setId/scripts/ai-edit`
 */
export async function aiEditPersonaScripts(
  body: AiEditPersonaScriptsRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(body.setId)}/scripts/ai-edit`,
    {
      method: "POST",
      body: JSON.stringify({
        tenantId: body.tenantId,
        setId: body.setId,
        scriptIds: body.scriptIds,
        instruction: body.instruction,
        idempotencyKey: crypto.randomUUID(),
        ...(body.cell ? { cell: body.cell } : {}),
      }),
    },
  );
  if (!correlationId) {
    throw new Error("AI edit response missing correlationId");
  }
  return { correlationId };
}

/**
 * Amber Path: generate a sketch or animatic storyboard for one persona script.
 * `POST /persona-opportunity-sets/:setId/scripts/:scriptId/storyboard`
 */
export async function generatePersonaStoryboard(
  setId: string,
  scriptId: string,
  tenantId: string,
  variant: "sketch" | "panels" = "sketch",
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/storyboard`,
    {
      method: "POST",
      body: JSON.stringify({
        tenantId,
        idempotencyKey: crypto.randomUUID(),
        variant,
      }),
    },
  );
  if (!correlationId) {
    throw new Error("Storyboard response missing correlationId");
  }
  return { correlationId };
}

export type PersonaScriptContentPatch =
  | { target: "hook"; value: string }
  | {
      target: "scene";
      sceneNumber: number;
      field: "seeing" | "hearing" | "product" | "props";
      value: string;
    };

/**
 * Persist a manual title or scene-cell edit (no AI).
 * `PATCH /persona-opportunity-sets/:setId/scripts/:scriptId/content`
 */
export async function patchPersonaScriptContent(
  setId: string,
  scriptId: string,
  tenantId: string,
  patch: PersonaScriptContentPatch,
): Promise<PersonaScriptDto> {
  const { data } = await authFetch<PersonaScriptDto>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/content`,
    {
      method: "PATCH",
      body: JSON.stringify({ tenantId, ...patch }),
    },
  );
  if (!data) {
    throw new Error("Manual script edit response missing data");
  }
  return data;
}

export interface PersonaScriptHookDraft {
  /** Omit for a new hook. */
  hookKey?: string;
  voiceover: string;
  onScreenText?: string | null;
  line?: string | null;
  strategyLabel?: string | null;
}

/**
 * Replace the ordered hook set; the first hook becomes the primary.
 * `PUT /persona-opportunity-sets/:setId/scripts/:scriptId/hooks`
 */
export async function replacePersonaScriptHooks(
  setId: string,
  scriptId: string,
  tenantId: string,
  expectedVersion: number,
  hooks: PersonaScriptHookDraft[],
): Promise<PersonaScriptDto> {
  const { data } = await authFetch<PersonaScriptDto>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/hooks`,
    {
      method: "PUT",
      body: JSON.stringify({ tenantId, expectedVersion, hooks }),
    },
  );
  if (!data) {
    throw new Error("Hook update response missing data");
  }
  return data;
}

/**
 * Persist favorite / unfavorite for one persona script.
 * `PATCH /persona-opportunity-sets/:setId/scripts/:scriptId/favorite`
 */
export async function togglePersonaScriptFavorite(
  setId: string,
  scriptId: string,
  tenantId: string,
  isFavorite: boolean,
): Promise<{ id: string; isFavorite: boolean }> {
  const { data } = await authFetch<{ id: string; isFavorite: boolean }>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/favorite`,
    {
      method: "PATCH",
      body: JSON.stringify({ tenantId, isFavorite }),
    },
  );
  if (!data) {
    throw new Error("Favorite script response missing data");
  }
  return data;
}

export interface DeletePersonaScriptsResult {
  deletedIds: string[];
}

/**
 * Soft-delete selected persona scripts (and live hook-variant children of parents).
 * `POST /persona-opportunity-sets/:setId/scripts/delete`
 */
export async function deletePersonaScripts(
  setId: string,
  body: { tenantId: string; idempotencyKey: string; scriptIds: string[] },
): Promise<DeletePersonaScriptsResult> {
  const { data } = await authFetch<DeletePersonaScriptsResult>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/delete`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  if (!data) {
    throw new Error("Delete scripts response missing data");
  }
  return data;
}

export interface SuggestPersonaScriptsRequest {
  tenantId: string;
  description: string;
  variations: number;
  referenceVideoUrl?: string;
  outputType?: "script" | "brief";
  youtubeFormat?: YoutubeFormat;
  /** Reused for the in-flight attempt so a double submit does not mint a second key. */
  idempotencyKey?: string;
}

/**
 * Kick off Add-your-script generation (Amber Path).
 * `POST /persona-opportunity-sets/:setId/scripts/suggest`
 */
export async function suggestPersonaScripts(
  setId: string,
  body: SuggestPersonaScriptsRequest,
): Promise<{ correlationId: string }> {
  const idempotencyKey = body.idempotencyKey ?? crypto.randomUUID();
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/suggest`,
    {
      method: "POST",
      body: JSON.stringify({
        tenantId: body.tenantId,
        setId,
        description: body.description,
        variations: body.variations,
        ...(body.referenceVideoUrl ? { referenceVideoUrl: body.referenceVideoUrl } : {}),
        outputType: body.outputType ?? "script",
        ...(body.youtubeFormat ? { youtubeFormat: body.youtubeFormat } : {}),
        idempotencyKey,
      }),
    },
  );
  if (!correlationId) {
    throw new Error("Suggest scripts response missing correlationId");
  }
  return { correlationId };
}

export interface PersonaResolvedLinkResult {
  url: string;
  ok: boolean;
  reason: string;
}

/**
 * Pre-fetch pasted reference URLs before Add-your-script generation (Amber Path).
 * `POST /persona-opportunity-sets/:setId/scripts/resolve-links`
 */
export async function resolvePersonaReferenceLinks(
  setId: string,
  referenceUrls: string[],
  tenantId: string,
): Promise<{ correlationId: string }> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/resolve-links`,
    {
      method: "POST",
      body: JSON.stringify({ tenantId, setId, referenceUrls, idempotencyKey }),
    },
  );
  if (!correlationId) {
    throw new Error("Resolve reference links response missing correlationId");
  }
  return { correlationId };
}

export type PersonaScriptVersionSource = "GENERATED" | "MANUAL" | "AI_EDIT" | "RESTORED";

export interface PersonaScriptIdentitySnapshot {
  adDescription: string | null;
  background: string | null;
  concept: string | null;
  duration: string | null;
  hook: string | null;
  presenter: string | null;
  product: string | null;
  rationale: string | null;
  script: string | null;
  sequence: string | null;
}

export interface PersonaScriptVersionEntryDto {
  id: string | null;
  version: number;
  source: PersonaScriptVersionSource;
  changeLabel: string | null;
  instruction: string | null;
  restoredFromVersion: number | null;
  createdAt: string;
  createdBy: string | null;
  identity: PersonaScriptIdentitySnapshot;
}

export interface PersonaScriptVersionHistoryDto {
  scriptId: string;
  versions: PersonaScriptVersionEntryDto[];
  activeVersion: number | null;
  version?: number;
  script?: PersonaScriptDto;
}

/**
 * List version rail entries (newest first), optionally overlay a version's identity + scenes.
 * `GET /persona-opportunity-sets/:setId/scripts/:scriptId/scene-versions`
 */
export async function fetchPersonaScriptVersionHistory(
  setId: string,
  scriptId: string,
  version?: number,
): Promise<PersonaScriptVersionHistoryDto> {
  const query = version != null ? `?version=${encodeURIComponent(String(version))}` : "";
  const { data } = await authFetch<PersonaScriptVersionHistoryDto>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/scene-versions${query}`,
  );
  if (!data) {
    throw new Error("Script version history response missing data");
  }
  return data;
}

/**
 * Append a Restored copy of the selected version.
 * `POST /persona-opportunity-sets/:setId/scripts/:scriptId/scene-versions/:version/restore`
 */
export async function restorePersonaScriptVersion(
  setId: string,
  scriptId: string,
  version: number,
  tenantId: string,
): Promise<PersonaScriptDto> {
  const { data } = await authFetch<PersonaScriptDto>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/scripts/${encodeURIComponent(scriptId)}/scene-versions/${encodeURIComponent(String(version))}/restore`,
    {
      method: "POST",
      body: JSON.stringify({ tenantId }),
    },
  );
  if (!data) {
    throw new Error("Restore script version response missing data");
  }
  return data;
}
